import React, { useEffect, useState } from 'react';
import Button from '@components/Button';
import { ResetPasswordForm } from '@helpers/Models';
import { errorMessage, responseTexts } from '@helpers/Constants';
import { Axios, getErrorMessage } from '@helpers/ApiService';
import { useNavigate } from 'react-router-dom';
import { ValidationService } from '@services/ValidationService';
import { useToast } from '@contexts/Toast';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { Logo } from '@components/Logo';

export const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const { showToastMessage } = useToast();

  const [pageDescription, setPageDescription] = useState('');
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [token, setToken] = useState('');
  const [formData, setFormData] = useState<ResetPasswordForm>({
    token: '',
    newPassword: '',
    confirmPassword: '',
  });

  /**
   * Extracts the reset token from the current URL and updates the state.
   *
   * This `useEffect` runs once when the component mounts. It checks if the URL contains
   * a token after `/reset/`. If a token is found, it sets the token state. If its failed
   * an error message will be shown.
   *
   * @returns {void}
   *
   */
  useEffect(() => {
    const currentUrl = window.location.href;

    //below is for invite user
    if (currentUrl.includes('/invite/')) {
      const parts = currentUrl.split('/invite/');
      if (parts.length > 1) {
        const token = parts[1];
        if (!token || token === '') {
          showToastMessage(errorMessage.INVALID_TOKEN, 'error');
        } else {
          setToken(token);
          setPageDescription('Please reset the password before logging in.');
        }
      } else {
        showToastMessage(errorMessage.INVALID_TOKEN, 'error');
      }
    }
    //below is for reset password
    else if (currentUrl.includes('/reset/')) {
      const parts = currentUrl.split('/reset/');
      if (parts.length > 1) {
        const token = parts[1];
        if (!token || token === '') {
          showToastMessage(errorMessage.INVALID_TOKEN, 'error');
        } else {
          //validate the token by calling the /validate-token endpoint
          Axios.post(ApiEndpoints.VALIDATE_TOKEN, { token })
            .then(() => {
              setToken(token);
            })
            .catch((error) => {
              showToastMessage(getErrorMessage(error), 'error');
              navigate(FrontendRoutes.LOGIN);
            });

          setPageDescription(
            `Enter a new password to reset the password on your account.\nWe’ll ask for this password whenever you log in.`,
          );
        }
      } else {
        showToastMessage(errorMessage.INVALID_TOKEN, 'error');
      }
    } else {
      showToastMessage(errorMessage.INVALID_TOKEN, 'error');
    }
  }, []);

  /**
   * Handles input changes for a form field, updating the form data and validating the input.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event - The change event triggered by the input field.
   *
   * - Extracts the `name` and `value` from the event's target.
   * - Updates the form data state with the new value.
   * - Validates the field using `ValidationService.validateField` and updates the error state accordingly.
   *
   * @returns {void}
   *
   */
  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Validate the field as user types
    const error = ValidationService.validateField(name, value, formData);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  /**
   * Handles the form submission for the Forgot Password form.
   *
   * @param {React.FormEvent} event - The form submission event.
   *
   * @returns {Promise<void>} A promise that resolves when the submission process completes.
   */
  const handleSubmit: (event: React.FormEvent) => Promise<void> = async (
    event,
  ) => {
    event.preventDefault();

    const { isValid, errors: validationErrors } =
      ValidationService.handleValidation(formData);
    setErrors(validationErrors);

    if (isValid) {
      try {
        const payload = { ...formData, token };

        await Axios.post(ApiEndpoints.RESET_PASSWORD, payload);
        showToastMessage(responseTexts.RESET_PASSWORD_SUCCESS, 'success');
        navigate(FrontendRoutes.LOGIN);
      } catch (error) {
        showToastMessage(getErrorMessage(error), 'error');
      }
    } else {
      showToastMessage(errorMessage.INVALID_FORM, 'error');
    }
  };

  return (
    <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-12 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-sm">
        <Logo />
        <h2 className="mt-6 text-center text-2xl/9 font-bold tracking-tight text-gray-900">
          Reset Your Password
        </h2>
        <span className="mt-2 text-sm text-center block">
          {pageDescription}
        </span>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label
              htmlFor="newPassword"
              className="block text-sm font-medium text-gray-900"
            >
              New Password *
            </label>
            <input
              id="newPassword"
              name="newPassword"
              type="password"
              onChange={handleInputChange}
              value={formData.newPassword}
              className={`block w-full rounded-md mt-[3px] px-3 py-1.5 text-base text-gray-900 outline outline-1 placeholder:text-gray-400 focus:outline-2 sm:text-sm ${
                errors.newPassword
                  ? 'outline-red-500 border-red-500  focus:outline-red-500'
                  : 'outline-gray-300 focus:outline-primary'
              }`}
            />
            {errors.newPassword && (
              <p className="text-red-600 text-sm">{errors.newPassword}</p>
            )}
          </div>
          <div>
            <label
              htmlFor="confirmPassword"
              className="block text-sm font-medium text-gray-900"
            >
              Confirm Password *
            </label>
            <input
              id="confirmPassword"
              name="confirmPassword"
              type="password"
              onChange={handleInputChange}
              value={formData.confirmPassword}
              className={`block w-full rounded-md mt-[3px] px-3 py-1.5 text-base text-gray-900 outline outline-1 placeholder:text-gray-400 focus:outline-2 sm:text-sm ${
                errors.confirmPassword
                  ? 'outline-red-500 border-red-500  focus:outline-red-500'
                  : 'outline-gray-300 focus:outline-primary'
              }`}
            />
            {errors.confirmPassword && (
              <p className="text-red-600 text-sm">{errors.confirmPassword}</p>
            )}
          </div>

          <div>
            <Button className="mt-6" name={'Continue'}></Button>
            <p className="text-gray-800 text-sm mt-3 text-center">
              Know your password?{' '}
              <a
                href="/login"
                className="text-primary font-semibold hover:underline ml-1"
              >
                Log in
              </a>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};
